/**
 * Exit-Ticket Formative Assessments
 * -----------------------------------
 * A quick-check (2-3 items) run immediately after an activity completes.
 * Goal: verify the child actually internalized the declared `learningObjectives`
 * of the activity/tier, not just went through the motions.
 *
 * Design principles (see docs/improvements/content/02-exit-tickets.md):
 *  - Pre-K tickets are NON-TEXT. Parent reads the prompt; child shows, says,
 *    draws, or does. Evaluation is by parent rubric, not auto-graded.
 *  - Kinder tickets mix show-and-tell with very short multiple-choice using
 *    pictures (options are still words here; the UI layer pairs them with
 *    icons/illustrations).
 *  - Early / Upper elementary can use multiple-choice, short-answer, or
 *    production tasks (do-it) with rubric scoring.
 *  - EVERY question is tied to one declared learning objective on the matching
 *    activity+tier. We do not re-test unrelated content.
 */

export type Tier = 'pre-k' | 'kinder' | 'early-elementary' | 'upper-elementary';

export type ExitTicketQuestionType =
  | 'multiple-choice'
  | 'draw'
  | 'show-and-tell'
  | 'do-it'
  | 'say-it';

export interface ExitTicketQuestion {
  /** Human-readable prompt. For pre-K, the parent reads this aloud. */
  prompt: string;
  /** Response modality. Drives the UI widget and grading path. */
  type: ExitTicketQuestionType;
  /** For multiple-choice only. Order-independent — correctIndex indexes into this array. */
  options?: string[];
  /** For multiple-choice only. Index of the correct option. */
  correctIndex?: number;
  /**
   * Parent-facing rubric. Used for every non-multiple-choice type, and also
   * offered as an override for multiple-choice when a child explains their
   * reasoning out loud. Short, observable, bias-free language.
   */
  parentCheckRubric?: string;
  /**
   * The exact learningObjective string this question assesses. Must match a
   * string in the activity+tier's `learningObjectives` array. Used by the
   * adaptive engine to route remediation to the right skill.
   */
  tiedObjective: string;
}

export interface ExitTicket {
  /** Matches `id` in src/data/activities/*.json */
  activityId: string;
  tier: Tier;
  /** 2-3 questions. Keep total time under ~90 seconds for pre-K, ~3 min for upper-elem. */
  questions: ExitTicketQuestion[];
}

/* ------------------------------------------------------------------ */
/* Exit ticket bank                                                   */
/* ------------------------------------------------------------------ */

export const exitTickets: ExitTicket[] = [
  /* ==================== MATH ==================== */
  {
    activityId: 'math-001', // Animal Counting Corral
    tier: 'pre-k',
    questions: [
      {
        prompt: 'Put exactly 4 animals in one bowl. Count them out loud as you go.',
        type: 'do-it',
        parentCheckRubric:
          'Child touches each animal once while saying the next number (one-to-one). Stops at 4.',
        tiedObjective: 'Count objects up to 10 with one-to-one correspondence',
      },
      {
        prompt: 'Point to the bowl that has the MOST animals.',
        type: 'show-and-tell',
        parentCheckRubric:
          'Child points to the correct bowl without recounting everything. Uses the word "most" or "more" if prompted.',
        tiedObjective: 'Compare groups using most, fewest, and same',
      },
    ],
  },
  {
    activityId: 'math-001',
    tier: 'kinder',
    questions: [
      {
        prompt: 'You counted 7 lions and 4 tigers. How many MORE lions than tigers?',
        type: 'multiple-choice',
        options: ['2', '3', '4', '11'],
        correctIndex: 1,
        tiedObjective: 'Compare quantities and find differences between groups',
      },
      {
        prompt: 'Draw a simple bar graph: 3 dogs, 5 cats, 2 birds.',
        type: 'draw',
        parentCheckRubric:
          'Three bars, roughly proportional heights (cats tallest, birds shortest). Axis labels optional.',
        tiedObjective: 'Create a simple bar graph from collected data',
      },
    ],
  },
  {
    activityId: 'math-001',
    tier: 'early-elementary',
    questions: [
      {
        prompt:
          'Trade rule: 3 dinosaurs = 1 dragon. If you trade away 9 dinosaurs, how many dragons do you get?',
        type: 'multiple-choice',
        options: ['2', '3', '6', '9'],
        correctIndex: 1,
        tiedObjective: 'Apply a 3-to-1 ratio to plan a multi-step trade',
      },
      {
        prompt:
          'Explain out loud: why is trading 9 dinosaurs for 3 dragons a FAIR trade using the 3-to-1 rule?',
        type: 'say-it',
        parentCheckRubric:
          'Child references the 3:1 ratio explicitly and shows that 9 ÷ 3 = 3. Mentions equal value on both sides.',
        tiedObjective: 'Justify a fair-trade decision using counts and reasoning',
      },
    ],
  },
  {
    activityId: 'math-001',
    tier: 'upper-elementary',
    questions: [
      {
        prompt: 'A data set is {4, 4, 4, 6, 2}. What is the MEAN?',
        type: 'multiple-choice',
        options: ['3', '4', '4.5', '6'],
        correctIndex: 1,
        tiedObjective: 'Calculate mean, median, and mode from a data set',
      },
      {
        prompt:
          'When is a pie chart a BETTER choice than a bar graph?',
        type: 'multiple-choice',
        options: [
          'When you want to compare exact counts',
          'When you want to show parts of a whole (percentages)',
          'When your data changes over time',
          'When you only have two data points',
        ],
        correctIndex: 1,
        tiedObjective: 'Create and interpret multiple graph types including pie charts',
      },
      {
        prompt:
          'Write one sentence: what conclusion did YOUR toy census support, and what evidence supports it?',
        type: 'say-it',
        parentCheckRubric:
          'Claim + supporting data point (e.g., "Most of my animals have 4 legs, because 12 out of 18 were 4-legged"). Evidence must be numeric.',
        tiedObjective: 'Write analytical conclusions supported by data evidence',
      },
    ],
  },
  {
    activityId: 'math-002', // Shape Scavenger Hunt
    tier: 'pre-k',
    questions: [
      {
        prompt: 'Point to something in this room that is a CIRCLE.',
        type: 'show-and-tell',
        parentCheckRubric: 'Child points to a round object (clock, plate, knob). Accepts any true circle.',
        tiedObjective: 'Recognize basic 2D shapes in everyday objects',
      },
      {
        prompt: 'Show me with your hands: what does a TRIANGLE look like?',
        type: 'do-it',
        parentCheckRubric: 'Child forms three sides/corners with fingers or traces a triangle in the air.',
        tiedObjective: 'Recognize basic 2D shapes in everyday objects',
      },
    ],
  },

  /* ==================== LITERACY ==================== */
  {
    activityId: 'lit-001', // Letter Hunt Safari
    tier: 'pre-k',
    questions: [
      {
        prompt: 'I am holding up the letter S. Show me something that starts with the S sound.',
        type: 'show-and-tell',
        parentCheckRubric:
          'Child names or points to an object whose spoken name begins with /s/ (sock, sun, stick). Ignore spelling quirks.',
        tiedObjective: 'Connect beginning letter sounds to real-world objects',
      },
      {
        prompt: 'Say the sound that this letter makes: M.',
        type: 'say-it',
        parentCheckRubric: 'Child produces the /m/ sound (not the letter name "em").',
        tiedObjective: 'Recognize and name at least 5 uppercase letters',
      },
    ],
  },
  {
    activityId: 'lit-001',
    tier: 'kinder',
    questions: [
      {
        prompt: 'Spell the word TREE the way it SOUNDS (it is okay to guess).',
        type: 'say-it',
        parentCheckRubric:
          'Any phonetic attempt is a pass: TRE, TREE, TREY, CHREE. We are checking sound-letter mapping, not convention.',
        tiedObjective: 'Practice inventive spelling and phonetic writing',
      },
      {
        prompt: 'Which word starts with the SAME sound as BIRD?',
        type: 'multiple-choice',
        options: ['cat', 'ball', 'snake', 'apple'],
        correctIndex: 1,
        tiedObjective: 'Associate all 26 letters with their sounds in real-world context',
      },
    ],
  },
  {
    activityId: 'lit-001',
    tier: 'early-elementary',
    questions: [
      {
        prompt: 'Write ONE simile about a leaf you found.',
        type: 'say-it',
        parentCheckRubric:
          'Response uses "like" or "as" to compare the leaf to something else (e.g., "crunchy like a cracker"). Reject bare adjectives.',
        tiedObjective: 'Use precise adjectives and similes in descriptive writing',
      },
      {
        prompt: 'In an alphabetical index, which word comes FIRST?',
        type: 'multiple-choice',
        options: ['pinecone', 'acorn', 'moss', 'bark'],
        correctIndex: 1,
        tiedObjective: 'Organize information into categories and create an alphabetical index',
      },
    ],
  },
  {
    activityId: 'lit-001',
    tier: 'upper-elementary',
    questions: [
      {
        prompt: 'The word "deciduous" comes from Latin "decidere" meaning "to fall off." Which tree is deciduous?',
        type: 'multiple-choice',
        options: [
          'A pine tree that stays green all year',
          'A maple tree that drops its leaves in fall',
          'A cactus that has no leaves',
          'A palm tree near the ocean',
        ],
        correctIndex: 1,
        tiedObjective: 'Research and apply word etymology to deepen vocabulary understanding',
      },
      {
        prompt:
          'Write ONE line that uses PERSONIFICATION about something you observed on your walk.',
        type: 'say-it',
        parentCheckRubric:
          'The non-human subject performs a human action (e.g., "the wind whispered"). Reject plain description.',
        tiedObjective: 'Use literary devices (simile, metaphor, personification) purposefully in writing',
      },
    ],
  },
  {
    activityId: 'lit-002', // Story Time Theater
    tier: 'pre-k',
    questions: [
      {
        prompt: 'Tell me what happened FIRST in the story, then in the MIDDLE, then at the END.',
        type: 'say-it',
        parentCheckRubric:
          'Three distinct events in correct sequence. Exact wording does not matter — order does.',
        tiedObjective: 'Retell a story in correct sequence (beginning, middle, end)',
      },
      {
        prompt: 'Who was the MAIN character in the story?',
        type: 'say-it',
        parentCheckRubric: 'Child names the protagonist (not a side character or object).',
        tiedObjective: 'Identify main characters in a story',
      },
    ],
  },

  /* ==================== SCIENCE ==================== */
  {
    activityId: 'sci-001', // Kitchen Volcano
    tier: 'pre-k',
    questions: [
      {
        prompt: 'Show me with your body how the volcano moved when it erupted.',
        type: 'do-it',
        parentCheckRubric:
          'Child mimics bubbling/overflowing (hands rising, fingers wiggling). Any embodied response counts.',
        tiedObjective: 'Practice scientific observation by describing what they see, hear, and feel',
      },
      {
        prompt:
          'Say one word about what the volcano SOUNDED like.',
        type: 'say-it',
        parentCheckRubric:
          'Sound word: fizzy, bubbly, hissing, popping, whooshing. Reject shape/color words.',
        tiedObjective: 'Observe and describe a chemical reaction using sensory language',
      },
    ],
  },
  {
    activityId: 'sci-001',
    tier: 'kinder',
    questions: [
      {
        prompt: 'Warm vinegar made a BIGGER eruption than cold vinegar. What was the CAUSE?',
        type: 'multiple-choice',
        options: [
          'The color of the vinegar',
          'The temperature of the vinegar',
          'The size of the cup',
          'The food coloring',
        ],
        correctIndex: 1,
        tiedObjective: 'Identify cause and effect in a simple chemical reaction',
      },
      {
        prompt:
          'If you want to test if MORE baking soda makes a BIGGER eruption, what is the ONE thing you change?',
        type: 'multiple-choice',
        options: [
          'The amount of baking soda',
          'The amount of vinegar',
          'Both at the same time',
          'The color',
        ],
        correctIndex: 0,
        tiedObjective: 'Test variables by changing one element of an experiment at a time',
      },
    ],
  },
  {
    activityId: 'sci-001',
    tier: 'early-elementary',
    questions: [
      {
        prompt: 'In a controlled experiment, how many variables should you change at one time?',
        type: 'multiple-choice',
        options: ['As many as you want', 'Exactly one', 'Zero', 'At least three'],
        correctIndex: 1,
        tiedObjective: 'Design and conduct a controlled experiment changing one variable at a time',
      },
      {
        prompt: 'Which section of a lab report contains your HYPOTHESIS?',
        type: 'multiple-choice',
        options: ['Results', 'Conclusion', 'Introduction/Question', 'Materials'],
        correctIndex: 2,
        tiedObjective: 'Write a structured lab report following the scientific method',
      },
      {
        prompt:
          'Show me your data table. Is the DEPENDENT variable (eruption height) in a column, and the INDEPENDENT variable (vinegar amount) in another?',
        type: 'show-and-tell',
        parentCheckRubric:
          'Table has labeled columns; each row is one trial. Eruption height values vary across trials.',
        tiedObjective: 'Collect quantitative data and organize it in tables and graphs',
      },
    ],
  },
  {
    activityId: 'sci-001',
    tier: 'upper-elementary',
    questions: [
      {
        prompt: 'A substance turns red cabbage juice bright pink. It is most likely:',
        type: 'multiple-choice',
        options: [
          'A strong base (pH ~12)',
          'Neutral (pH 7)',
          'A strong acid (pH ~2)',
          'Not testable',
        ],
        correctIndex: 2,
        tiedObjective: 'Understand the pH scale and classify substances as acids or bases',
      },
      {
        prompt:
          'In NaHCO3 + CH3COOH → NaC2H3O2 + H2O + CO2, which product causes the eruption?',
        type: 'multiple-choice',
        options: ['NaC2H3O2 (sodium acetate)', 'H2O (water)', 'CO2 (carbon dioxide gas)', 'NaHCO3'],
        correctIndex: 2,
        tiedObjective: 'Explain chemical reactions at a molecular level using chemical formulas',
      },
    ],
  },
  {
    activityId: 'sci-002', // Sink or Float
    tier: 'pre-k',
    questions: [
      {
        prompt: 'Pick up this spoon. Will it SINK or FLOAT? Now try it.',
        type: 'do-it',
        parentCheckRubric:
          'Child makes a prediction aloud BEFORE dropping it. Credit the prediction and willingness to test, not correctness.',
        tiedObjective: 'Make predictions and test them through direct observation',
      },
      {
        prompt: 'Show me something from this room you think will FLOAT.',
        type: 'show-and-tell',
        parentCheckRubric:
          'Child picks a low-density object (plastic, wood, foam). If they pick metal, ask "why?" — reasoning counts too.',
        tiedObjective: 'Sort objects into categories based on physical properties',
      },
    ],
  },

  /* ==================== CODING ==================== */
  {
    activityId: 'code-001', // Robot Parent
    tier: 'pre-k',
    questions: [
      {
        prompt:
          'I am the robot and I am facing the couch. Tell me EXACTLY how to get to the door.',
        type: 'say-it',
        parentCheckRubric:
          'Child gives at least 2 sequenced commands (e.g., "turn, then walk"). Specific step counts are a bonus, not required.',
        tiedObjective: 'Break tasks into simple, sequential instructions',
      },
      {
        prompt: 'If you say "walk" but do NOT say "stop," what will the robot do?',
        type: 'say-it',
        parentCheckRubric:
          'Child answers that the robot keeps walking / walks into something. Shows understanding of literal execution.',
        tiedObjective: 'Understand that computers follow instructions exactly as given',
      },
    ],
  },
  {
    activityId: 'code-001',
    tier: 'kinder',
    questions: [
      {
        prompt:
          'Your program is: FORWARD, FORWARD, TURN RIGHT, FORWARD. The robot walked into a chair on step 2. What do you do?',
        type: 'multiple-choice',
        options: [
          'Run the program again exactly the same way',
          'Change or remove the step that caused the crash',
          'Give up',
          'Add 10 more FORWARD steps',
        ],
        correctIndex: 1,
        tiedObjective: 'Debug a program by identifying and fixing incorrect instructions',
      },
      {
        prompt: 'Show me your command cards in the ORDER the robot should do them.',
        type: 'show-and-tell',
        parentCheckRubric:
          'Cards are arranged left-to-right or top-to-bottom in execution order. Child can point to "first" and "last."',
        tiedObjective: 'Write sequential commands using an instruction set',
      },
    ],
  },
  {
    activityId: 'code-001',
    tier: 'early-elementary',
    questions: [
      {
        prompt: 'Rewrite this using a REPEAT loop: FORWARD, FORWARD, FORWARD, FORWARD.',
        type: 'say-it',
        parentCheckRubric:
          'Answer is equivalent to REPEAT(4) DO(FORWARD) or REPEAT 4 TIMES: FORWARD. Accept any clear loop syntax.',
        tiedObjective: 'Apply loops (REPEAT) to reduce repetition in sequential instructions',
      },
      {
        prompt: 'IF the door is closed THEN _____. What is the best completion?',
        type: 'multiple-choice',
        options: ['OPEN the door', 'FORWARD', 'REPEAT', 'PICK_UP'],
        correctIndex: 0,
        tiedObjective: 'Use conditional statements (IF-THEN) to handle variable conditions in a program',
      },
    ],
  },
  {
    activityId: 'code-001',
    tier: 'upper-elementary',
    questions: [
      {
        prompt:
          'In your language: SET count = 0. After PICK_UP runs 3 times inside a loop that does count = count + 1, what is count?',
        type: 'multiple-choice',
        options: ['0', '1', '3', 'undefined'],
        correctIndex: 2,
        tiedObjective:
          'Design a programming language with consistent syntax including variables, loops, and conditionals',
      },
      {
        prompt:
          'Your sibling cannot run your program because the command "GRAB" is not in the reference manual. What does this tell you?',
        type: 'multiple-choice',
        options: [
          'The program is fine; they are wrong',
          'Your reference manual is incomplete — every command used must be documented',
          'You need a bigger program',
          'Nothing; skip that line',
        ],
        correctIndex: 1,
        tiedObjective:
          'Write technical documentation (a language reference manual) that enables others to use the language',
      },
    ],
  },

  /* ==================== ENGINEERING ==================== */
  {
    activityId: 'eng-001', // Bridge Builder
    tier: 'pre-k',
    questions: [
      {
        prompt: 'Show me your bridge. Can you push down on it gently? Does it hold?',
        type: 'do-it',
        parentCheckRubric:
          'Child applies light pressure and reports "yes/no." Any test attempt passes; we are checking the habit of testing.',
        tiedObjective: 'Test structures to see if they hold weight',
      },
      {
        prompt: 'What shape holds the bridge up from underneath?',
        type: 'say-it',
        parentCheckRubric:
          'Names a supporting shape — pillar, block, triangle, arch. Gesturing the shape counts.',
        tiedObjective: 'Identify basic structural shapes used in building',
      },
    ],
  },
  {
    activityId: 'eng-001',
    tier: 'early-elementary',
    questions: [
      {
        prompt: 'Which shape is STRONGEST for holding weight on a bridge?',
        type: 'multiple-choice',
        options: ['Circle', 'Square', 'Triangle', 'Star'],
        correctIndex: 2,
        tiedObjective: 'Identify which structural shapes distribute weight most effectively',
      },
      {
        prompt: 'Your bridge collapsed. Explain ONE change you will make and WHY.',
        type: 'say-it',
        parentCheckRubric:
          'Child names a specific change ("more supports under the middle") and a reason ("the middle sagged"). Iteration + reasoning.',
        tiedObjective: 'Iterate on a design based on test results',
      },
    ],
  },

  /* ==================== EQ (Emotional Intelligence) ==================== */
  {
    activityId: 'eq-001', // Weather Feelings
    tier: 'pre-k',
    questions: [
      {
        prompt: 'Show me your FACE when you feel HAPPY. Now show me SAD.',
        type: 'do-it',
        parentCheckRubric:
          'Two clearly different facial expressions. Smile vs. frown. Accept any visible contrast.',
        tiedObjective: 'Identify and express basic emotions through facial expressions',
      },
      {
        prompt:
          'If today were a weather, what weather would match how you feel right now?',
        type: 'say-it',
        parentCheckRubric:
          'Child names a weather (sunny, rainy, cloudy, stormy) and — if prompted — a reason. Reason is a bonus.',
        tiedObjective: 'Connect feelings to concrete metaphors',
      },
    ],
  },
  {
    activityId: 'eq-001',
    tier: 'kinder',
    questions: [
      {
        prompt: 'A friend took your toy without asking. Which feeling word fits BEST?',
        type: 'multiple-choice',
        options: ['Proud', 'Frustrated', 'Sleepy', 'Excited'],
        correctIndex: 1,
        tiedObjective: 'Match feeling words to situations that might cause them',
      },
      {
        prompt: 'Tell me about a time you felt calm. What helped?',
        type: 'say-it',
        parentCheckRubric:
          'Child describes a situation plus a calming strategy (deep breath, hug, quiet time). Either half counts.',
        tiedObjective: 'Describe strategies that help regulate emotions',
      },
    ],
  },

  /* ==================== FINANCIAL ==================== */
  {
    activityId: 'fin-001', // Store Play
    tier: 'pre-k',
    questions: [
      {
        prompt: 'This item costs 2 coins. Hand me 2 coins.',
        type: 'do-it',
        parentCheckRubric: 'Child counts out exactly 2 coins and hands them over.',
        tiedObjective: 'Exchange coins for goods in a pretend transaction',
      },
      {
        prompt: 'What do you SAY when you buy something from the store?',
        type: 'say-it',
        parentCheckRubric:
          'Any pro-social script: "please," "thank you," "how much?" The point is manners + ritual, not a specific line.',
        tiedObjective: 'Practice polite social exchange during transactions',
      },
    ],
  },
  {
    activityId: 'fin-003', // Wants vs Needs
    tier: 'kinder',
    questions: [
      {
        prompt: 'Which of these is a NEED?',
        type: 'multiple-choice',
        options: ['Candy', 'Water', 'A new toy', 'A video game'],
        correctIndex: 1,
        tiedObjective: 'Distinguish between wants and needs',
      },
      {
        prompt: 'Name one WANT and one NEED in your life.',
        type: 'say-it',
        parentCheckRubric:
          'Correctly classifies both. If the child calls "food" a want, gently probe; accept reasoning.',
        tiedObjective: 'Distinguish between wants and needs',
      },
    ],
  },
  {
    activityId: 'fin-004', // Lemonade Stand
    tier: 'early-elementary',
    questions: [
      {
        prompt:
          'Lemons cost $2. You sold 10 cups at $0.50 each. What is your PROFIT?',
        type: 'multiple-choice',
        options: ['$3', '$5', '$7', '$10'],
        correctIndex: 0,
        tiedObjective: 'Calculate profit as revenue minus costs',
      },
      {
        prompt:
          'Your neighbor sells lemonade for $0.25. Should you LOWER your price, RAISE it, or KEEP it? Why?',
        type: 'say-it',
        parentCheckRubric:
          'Child commits to a choice AND gives a reason tied to customers/competition/costs. Any defended answer passes.',
        tiedObjective: 'Use simple market reasoning to set prices',
      },
    ],
  },

  /* ==================== BODY WELLNESS ==================== */
  {
    activityId: 'bw-002', // Five Senses
    tier: 'pre-k',
    questions: [
      {
        prompt: 'Close your eyes. I will give you something to smell. Tell me what you smell.',
        type: 'do-it',
        parentCheckRubric:
          'Child keeps eyes closed and names the smell or a similar word. Any description of the scent passes.',
        tiedObjective: 'Use the five senses to explore the world',
      },
      {
        prompt: 'Which body part do you use to HEAR?',
        type: 'say-it',
        parentCheckRubric: 'Child says/points to ears.',
        tiedObjective: 'Match each sense to its body part',
      },
    ],
  },
  {
    activityId: 'bw-003', // Skeleton Crew
    tier: 'kinder',
    questions: [
      {
        prompt: 'Touch your RIBS. Touch your SKULL.',
        type: 'do-it',
        parentCheckRubric: 'Child touches roughly correct areas — chest for ribs, head for skull.',
        tiedObjective: 'Identify major bones in the human body',
      },
      {
        prompt: 'What do bones DO for our body?',
        type: 'multiple-choice',
        options: ['Help us think', 'Give our body shape and protect organs', 'Digest food', 'Pump blood'],
        correctIndex: 1,
        tiedObjective: 'Understand the function of the skeleton',
      },
    ],
  },

  /* ==================== CREATIVE ==================== */
  {
    activityId: 'cre-001', // Playdough Creations
    tier: 'pre-k',
    questions: [
      {
        prompt: 'Show me what you made and tell me its NAME.',
        type: 'show-and-tell',
        parentCheckRubric:
          'Child shares their creation and gives it a name or identity. Creative ownership, not accuracy.',
        tiedObjective: 'Express ideas through 3D sculpting with playdough',
      },
      {
        prompt: 'Make a BIG ball and a SMALL ball.',
        type: 'do-it',
        parentCheckRubric: 'Two clearly different-sized balls. Fine motor + size comparison.',
        tiedObjective: 'Practice fine motor skills through squeezing, rolling, and pinching',
      },
    ],
  },
  {
    activityId: 'cre-003', // Smoothie Making
    tier: 'early-elementary',
    questions: [
      {
        prompt:
          'You put in 1 banana, 1 cup of berries, and 1 cup of milk. Name a change you could try next time.',
        type: 'say-it',
        parentCheckRubric:
          'Child proposes ONE ingredient swap or ratio change with reasoning. Evidence of iteration.',
        tiedObjective: 'Follow a recipe and iterate with substitutions',
      },
      {
        prompt: 'Why do we measure ingredients instead of just guessing?',
        type: 'multiple-choice',
        options: [
          'So the smoothie tastes the same each time',
          'Measuring is faster',
          'It makes the blender stronger',
          'It is required by law',
        ],
        correctIndex: 0,
        tiedObjective: 'Understand why consistent measurement matters in cooking',
      },
    ],
  },

  /* ==================== LANGUAGE ==================== */
  {
    activityId: 'lang-animal-kingdom-1',
    tier: 'pre-k',
    questions: [
      {
        prompt: 'Say the sound a LION makes. Now say the sound an ELEPHANT makes.',
        type: 'say-it',
        parentCheckRubric: 'Child produces two different animal vocalizations.',
        tiedObjective: 'Build vocabulary for common animals through sound and word play',
      },
      {
        prompt: 'Point to the animal that has STRIPES.',
        type: 'show-and-tell',
        parentCheckRubric:
          'Child points to a zebra/tiger image or names one if no image is present. Vocabulary + attribute match.',
        tiedObjective: 'Match animal names to visual attributes',
      },
    ],
  },
  {
    activityId: 'lang-space-1',
    tier: 'kinder',
    questions: [
      {
        prompt: 'Which of these is a PLANET?',
        type: 'multiple-choice',
        options: ['The Sun', 'Mars', 'A comet', 'The Moon'],
        correctIndex: 1,
        tiedObjective: 'Learn space vocabulary: planet, star, moon, orbit',
      },
      {
        prompt: 'Use the word ORBIT in a sentence.',
        type: 'say-it',
        parentCheckRubric:
          'Sentence uses "orbit" correctly (something goes around something else). Simple structure is fine.',
        tiedObjective: 'Use new vocabulary in original sentences',
      },
    ],
  },

  /* ==================== PHYSICAL ==================== */
  {
    activityId: 'phys-001', // Animal Movement Relay
    tier: 'pre-k',
    questions: [
      {
        prompt: 'Move like a FROG to the door and back.',
        type: 'do-it',
        parentCheckRubric: 'Child hops/squats, not just walks. Any frog-style motion counts.',
        tiedObjective: 'Imitate animal movements using gross motor skills',
      },
      {
        prompt: 'Show me how you move like a SNAKE.',
        type: 'do-it',
        parentCheckRubric: 'Child wriggles/slithers on the floor or with an arm. Low, sinuous movement.',
        tiedObjective: 'Explore different locomotion patterns',
      },
    ],
  },
  {
    activityId: 'phys-004', // Ball Skills Circuit
    tier: 'kinder',
    questions: [
      {
        prompt: 'Bounce the ball 3 times in a row without losing control.',
        type: 'do-it',
        parentCheckRubric: 'Three consecutive controlled bounces. Minor recovery is okay.',
        tiedObjective: 'Develop hand-eye coordination through bouncing and catching',
      },
      {
        prompt: 'Name ONE thing that helps you catch better.',
        type: 'say-it',
        parentCheckRubric:
          'Answers like "watch the ball," "hands ready," "soft hands." Strategy awareness.',
        tiedObjective: 'Reflect on body mechanics that improve performance',
      },
    ],
  },

  /* ==================== SCAVENGER HUNTS ==================== */
  {
    activityId: 'hunt-001', // Tree Detective
    tier: 'pre-k',
    questions: [
      {
        prompt: 'Point to a tree LEAF. Point to a tree TRUNK.',
        type: 'show-and-tell',
        parentCheckRubric: 'Correct identification of both parts.',
        tiedObjective: 'Identify basic parts of a tree',
      },
      {
        prompt: 'Touch the bark. Tell me ONE word for how it feels.',
        type: 'say-it',
        parentCheckRubric:
          'Texture word: rough, bumpy, scratchy, soft. Sensory vocabulary.',
        tiedObjective: 'Describe textures using sensory language',
      },
    ],
  },
  {
    activityId: 'hunt-002', // Bug Safari
    tier: 'early-elementary',
    questions: [
      {
        prompt: 'How many LEGS does an insect have?',
        type: 'multiple-choice',
        options: ['4', '6', '8', '10'],
        correctIndex: 1,
        tiedObjective: 'Learn the defining traits of insects',
      },
      {
        prompt: 'Is a SPIDER an insect? Why or why not?',
        type: 'say-it',
        parentCheckRubric:
          'Correct answer: No — 8 legs, or spiders are arachnids. Either form of reasoning passes.',
        tiedObjective: 'Classify small creatures into insect vs. non-insect groups',
      },
    ],
  },

  /* ==================== SOCIAL STUDIES ==================== */
  {
    activityId: 'ss-001', // Map My Neighborhood
    tier: 'pre-k',
    questions: [
      {
        prompt: 'Draw a picture of your HOUSE and one place near it (park, store, school).',
        type: 'draw',
        parentCheckRubric:
          'Two distinct places represented — spatial idea of "near" more important than drawing quality.',
        tiedObjective: 'Understand that places can be represented on a map',
      },
      {
        prompt: 'Point in the direction of your front door.',
        type: 'show-and-tell',
        parentCheckRubric: 'Correct general direction. Mental map of home.',
        tiedObjective: 'Develop spatial awareness of familiar places',
      },
    ],
  },
  {
    activityId: 'ss-002', // Community Helpers
    tier: 'kinder',
    questions: [
      {
        prompt: 'Who would you call if there was a fire?',
        type: 'multiple-choice',
        options: ['A teacher', 'A firefighter', 'A librarian', 'A chef'],
        correctIndex: 1,
        tiedObjective: 'Match community helpers to the problems they solve',
      },
      {
        prompt: 'Name one thing a DOCTOR does to help people.',
        type: 'say-it',
        parentCheckRubric:
          'Any plausible role: checks health, gives medicine, fixes broken bones.',
        tiedObjective: 'Understand the role of community helpers',
      },
    ],
  },
  {
    activityId: 'ss-003', // Cultural Food Tasting
    tier: 'early-elementary',
    questions: [
      {
        prompt:
          'Name ONE food from a culture different from yours and the country it comes from.',
        type: 'say-it',
        parentCheckRubric:
          'Valid food + country pairing (sushi/Japan, tacos/Mexico, injera/Ethiopia). Geographic accuracy counts.',
        tiedObjective: 'Connect foods to the cultures and regions they come from',
      },
      {
        prompt: 'Why might two families eat different foods for dinner?',
        type: 'multiple-choice',
        options: [
          'One family is wrong',
          'Different cultures, traditions, and tastes',
          'The food is broken',
          'Only one food exists in the world',
        ],
        correctIndex: 1,
        tiedObjective: 'Appreciate cultural differences without ranking them',
      },
    ],
  },

  /* ==================== VISUAL ARTS ==================== */
  {
    activityId: 'va-001', // Frame Hunt
    tier: 'early-elementary',
    questions: [
      {
        prompt:
          'In the rule of thirds, where should the MAIN SUBJECT of your photo go?',
        type: 'multiple-choice',
        options: [
          'Exactly in the center',
          'On one of the third-lines or intersections',
          'In the corner',
          'It does not matter',
        ],
        correctIndex: 1,
        tiedObjective: 'Apply basic composition rules when framing images',
      },
      {
        prompt:
          'Show me one of your 10 compositions and tell me WHY you framed it that way.',
        type: 'show-and-tell',
        parentCheckRubric:
          'Child articulates a composition choice (leading lines, balance, framing, subject placement). Any deliberate reasoning passes.',
        tiedObjective: 'Explain compositional choices in their own photographs',
      },
    ],
  },
  {
    activityId: 'va-002', // Shot Types
    tier: 'upper-elementary',
    questions: [
      {
        prompt: 'Which shot type shows a character from head to toe with their surroundings?',
        type: 'multiple-choice',
        options: ['Close-up', 'Medium shot', 'Wide / long shot', 'Extreme close-up'],
        correctIndex: 2,
        tiedObjective: 'Identify and name common camera shot types',
      },
      {
        prompt: 'If you want to show a character\'s EMOTION, which shot works best and why?',
        type: 'say-it',
        parentCheckRubric:
          'Child picks close-up / extreme close-up AND explains that it shows facial detail.',
        tiedObjective: 'Match shot type to storytelling intent',
      },
    ],
  },
  {
    activityId: 'va-004', // Storyboard a Joke
    tier: 'upper-elementary',
    questions: [
      {
        prompt: 'A storyboard panel should show:',
        type: 'multiple-choice',
        options: [
          'Every word of dialogue written out',
          'One key visual moment from the scene',
          'The entire script',
          'Only a color',
        ],
        correctIndex: 1,
        tiedObjective: 'Translate a narrative beat into a single visual frame',
      },
      {
        prompt:
          'Why is the LAST panel of a joke storyboard the most important?',
        type: 'say-it',
        parentCheckRubric:
          'Child connects last panel to punchline / payoff / reveal. Understands narrative structure.',
        tiedObjective: 'Structure a joke visually with setup and punchline',
      },
    ],
  },

  /* ==================== ADDITIONAL COVERAGE ==================== */
  {
    activityId: 'math-003', // Pattern Block Designs
    tier: 'pre-k',
    questions: [
      {
        prompt: 'I made this pattern: red, blue, red, blue, ___ . What comes next?',
        type: 'say-it',
        parentCheckRubric: 'Child says or picks red. Pattern completion.',
        tiedObjective: 'Recognize and extend simple AB patterns',
      },
      {
        prompt: 'Make your OWN pattern with these shapes.',
        type: 'do-it',
        parentCheckRubric:
          'Child creates any repeating unit (AB, AAB, ABC). Must repeat at least twice.',
        tiedObjective: 'Create original repeating patterns',
      },
    ],
  },
  {
    activityId: 'code-002', // Pattern Trains
    tier: 'kinder',
    questions: [
      {
        prompt: 'This pattern is red-red-blue-red-red-blue. What is the RULE?',
        type: 'multiple-choice',
        options: ['AB', 'AAB', 'ABC', 'ABB'],
        correctIndex: 1,
        tiedObjective: 'Label a pattern rule using letter codes',
      },
      {
        prompt: 'Make an AAB pattern with any objects in this room.',
        type: 'do-it',
        parentCheckRubric: 'Three-unit repeat of form AAB, at least two full cycles.',
        tiedObjective: 'Build patterns that match a given rule',
      },
    ],
  },
  {
    activityId: 'eng-002', // Marble Run Engineer
    tier: 'early-elementary',
    questions: [
      {
        prompt: 'Your marble stopped halfway. What is the MOST LIKELY cause?',
        type: 'multiple-choice',
        options: [
          'The marble is too happy',
          'Not enough slope / the track was flat there',
          'Too much gravity',
          'The marble forgot the rules',
        ],
        correctIndex: 1,
        tiedObjective: 'Diagnose why a design fails using physics reasoning',
      },
      {
        prompt: 'Show me the steepest part of your run and the flattest part.',
        type: 'show-and-tell',
        parentCheckRubric: 'Child correctly identifies two slopes and can compare them.',
        tiedObjective: 'Recognize how slope affects speed',
      },
    ],
  },
  {
    activityId: 'eq-002', // Kindness Chain
    tier: 'pre-k',
    questions: [
      {
        prompt: 'Tell me ONE kind thing you did today.',
        type: 'say-it',
        parentCheckRubric: 'Any pro-social action: sharing, helping, hugging, listening.',
        tiedObjective: 'Identify and recount acts of kindness',
      },
      {
        prompt: 'Show me a kind face for a friend who is sad.',
        type: 'do-it',
        parentCheckRubric:
          'Soft expression, possibly a gesture (hand on heart, open arms). Empathy signal.',
        tiedObjective: 'Express empathy through facial expression and body language',
      },
    ],
  },
  {
    activityId: 'lit-003', // Rhyming Race
    tier: 'pre-k',
    questions: [
      {
        prompt: 'Give me a word that rhymes with CAT.',
        type: 'say-it',
        parentCheckRubric:
          'Any true rhyme (hat, bat, sat, mat, even nonsense like "zat"). Rhyme is the skill, not real words.',
        tiedObjective: 'Produce words that rhyme with a given word',
      },
      {
        prompt: 'Do these two words rhyme? DOG and LOG.',
        type: 'multiple-choice',
        options: ['Yes', 'No'],
        correctIndex: 0,
        tiedObjective: 'Identify whether two words rhyme',
      },
    ],
  },
  {
    activityId: 'lit-004', // Sight Word Treasure Hunt
    tier: 'kinder',
    questions: [
      {
        prompt: 'Read this word out loud: "the"',
        type: 'say-it',
        parentCheckRubric: 'Child reads it instantly as a sight word, not sounded out.',
        tiedObjective: 'Recognize common sight words on sight',
      },
      {
        prompt: 'Which of these is the word "and"?',
        type: 'multiple-choice',
        options: ['and', 'end', 'sand', 'add'],
        correctIndex: 0,
        tiedObjective: 'Distinguish sight words from visually similar words',
      },
    ],
  },
  {
    activityId: 'sci-003', // Weather Station Builder
    tier: 'early-elementary',
    questions: [
      {
        prompt: 'What tool measures TEMPERATURE?',
        type: 'multiple-choice',
        options: ['Rain gauge', 'Thermometer', 'Wind vane', 'Barometer'],
        correctIndex: 1,
        tiedObjective: 'Match weather instruments to what they measure',
      },
      {
        prompt: 'You recorded 3 days of temperatures: 60°F, 65°F, 70°F. Describe the TREND.',
        type: 'say-it',
        parentCheckRubric:
          'Child says "going up / warming / increasing." Trend vocabulary.',
        tiedObjective: 'Identify trends in recorded weather data',
      },
    ],
  },
  {
    activityId: 'sci-004', // Root Watch
    tier: 'pre-k',
    questions: [
      {
        prompt: 'Show me where the ROOTS are on your seed plant.',
        type: 'show-and-tell',
        parentCheckRubric: 'Points to roots (downward-growing white strands).',
        tiedObjective: 'Identify parts of a growing plant',
      },
      {
        prompt: 'What do roots do for a plant?',
        type: 'say-it',
        parentCheckRubric:
          'Any of: drink water, hold the plant down, get food from dirt. Partial credit for gestures.',
        tiedObjective: 'Understand the function of roots',
      },
    ],
  },
  {
    activityId: 'fin-002', // Coin Sorting
    tier: 'pre-k',
    questions: [
      {
        prompt: 'Show me a PENNY. Show me a QUARTER.',
        type: 'show-and-tell',
        parentCheckRubric: 'Correct identification of both coins.',
        tiedObjective: 'Identify U.S. coins by name',
      },
      {
        prompt: 'Which coin is WORTH THE MOST?',
        type: 'multiple-choice',
        options: ['Penny', 'Nickel', 'Dime', 'Quarter'],
        correctIndex: 3,
        tiedObjective: 'Rank coins by their value',
      },
    ],
  },
  {
    activityId: 'bw-001', // Head to Toe
    tier: 'pre-k',
    questions: [
      {
        prompt: 'Touch your head, then your knees, then your toes.',
        type: 'do-it',
        parentCheckRubric: 'Correct body parts in correct order.',
        tiedObjective: 'Identify and locate major body parts',
      },
      {
        prompt: 'How many fingers do you have on ONE hand?',
        type: 'say-it',
        parentCheckRubric: 'Child says 5. Counting on fingers is fine.',
        tiedObjective: 'Count body parts accurately',
      },
    ],
  },
  {
    activityId: 'cre-002', // Cardboard Box Build
    tier: 'kinder',
    questions: [
      {
        prompt:
          'Tell me what your box became, and ONE change you made while building.',
        type: 'say-it',
        parentCheckRubric:
          'Names the creation + describes one design iteration. Reflection on process.',
        tiedObjective: 'Reflect on design decisions during a build',
      },
      {
        prompt: 'Show me how your box stands up WITHOUT falling.',
        type: 'show-and-tell',
        parentCheckRubric: 'Structure is stable for at least 5 seconds.',
        tiedObjective: 'Build a structure that supports its own weight',
      },
    ],
  },
  {
    activityId: 'hunt-003', // Rock Collection
    tier: 'early-elementary',
    questions: [
      {
        prompt:
          'Sort your rocks into TWO groups using any rule you choose. Tell me your rule.',
        type: 'do-it',
        parentCheckRubric:
          'Rocks physically separated + rule stated (color, size, smoothness). Classification thinking.',
        tiedObjective: 'Classify rocks based on observable properties',
      },
      {
        prompt: 'Name TWO properties you could use to describe a rock.',
        type: 'say-it',
        parentCheckRubric:
          'Two distinct properties: color, texture, weight, shape, shine, size.',
        tiedObjective: 'Describe rocks using multiple observable properties',
      },
    ],
  },
  {
    activityId: 'ss-004', // Family Tree Art
    tier: 'kinder',
    questions: [
      {
        prompt: 'Who are YOUR parents\' parents called?',
        type: 'multiple-choice',
        options: ['Cousins', 'Grandparents', 'Siblings', 'Friends'],
        correctIndex: 1,
        tiedObjective: 'Learn family-relationship vocabulary',
      },
      {
        prompt: 'Show me where YOU go on your family tree.',
        type: 'show-and-tell',
        parentCheckRubric: 'Child points to the correct position (below parents).',
        tiedObjective: 'Understand generational structure in a family tree',
      },
    ],
  },
  {
    activityId: 'phys-002', // Obstacle Course
    tier: 'kinder',
    questions: [
      {
        prompt:
          'Walk me through your obstacle course and say each action: "jump," "crawl," "run."',
        type: 'do-it',
        parentCheckRubric: 'Child narrates while moving. Verb + action coordination.',
        tiedObjective: 'Coordinate body movement with verbal narration',
      },
      {
        prompt: 'Which part of your course was HARDEST? Why?',
        type: 'say-it',
        parentCheckRubric: 'Identifies one station and gives a body-based reason.',
        tiedObjective: 'Reflect on physical challenge and body feedback',
      },
    ],
  },
  {
    activityId: 'lang-animal-kingdom-3', // Animal Riddle Challenge
    tier: 'early-elementary',
    questions: [
      {
        prompt:
          'I have a long trunk, big ears, and I am gray. What am I?',
        type: 'say-it',
        parentCheckRubric: 'Child says elephant. Listening + inference.',
        tiedObjective: 'Solve riddles by combining clues',
      },
      {
        prompt: 'Make up a riddle for a CAT. Give me 2 clues.',
        type: 'say-it',
        parentCheckRubric:
          'Two true clues about cats without naming the animal. Creative production.',
        tiedObjective: 'Compose original riddles using descriptive clues',
      },
    ],
  },
  {
    activityId: 'ss-002',
    tier: 'pre-k',
    questions: [
      {
        prompt: 'Who helps you when you are sick?',
        type: 'say-it',
        parentCheckRubric: 'Doctor, nurse, parent. Any caregiver.',
        tiedObjective: 'Recognize people who help us',
      },
      {
        prompt: 'Show me how a police officer waves to say STOP.',
        type: 'do-it',
        parentCheckRubric: 'Palm-out stop gesture.',
        tiedObjective: 'Imitate helpers in pretend play',
      },
    ],
  },
  {
    activityId: 'fin-004',
    tier: 'upper-elementary',
    questions: [
      {
        prompt:
          'You can sell 20 cups at $0.50 OR 40 cups at $0.30. Which brings in more REVENUE?',
        type: 'multiple-choice',
        options: ['20 at $0.50 ($10)', '40 at $0.30 ($12)', 'They are equal', 'Cannot tell'],
        correctIndex: 1,
        tiedObjective: 'Compare pricing strategies by calculating total revenue',
      },
      {
        prompt:
          'Revenue is not the same as profit. Why not?',
        type: 'say-it',
        parentCheckRubric:
          'Child explains that profit = revenue – costs. Names at least one cost (cups, lemons, sugar).',
        tiedObjective: 'Distinguish revenue from profit',
      },
    ],
  },
  {
    activityId: 'bw-004', // Muscle Mission
    tier: 'early-elementary',
    questions: [
      {
        prompt: 'Flex your BICEP. Where did the muscle get bigger?',
        type: 'show-and-tell',
        parentCheckRubric: 'Child flexes arm and points to upper-arm bulge.',
        tiedObjective: 'Locate and name major muscle groups',
      },
      {
        prompt: 'Why do muscles get TIRED after lots of work?',
        type: 'multiple-choice',
        options: [
          'They run out of oxygen and fuel',
          'They turn into bones',
          'They fall off',
          'They get angry',
        ],
        correctIndex: 0,
        tiedObjective: 'Understand why muscles fatigue with effort',
      },
    ],
  },
  {
    activityId: 'hunt-004', // Bird Watching
    tier: 'kinder',
    questions: [
      {
        prompt: 'Name TWO parts of a bird.',
        type: 'say-it',
        parentCheckRubric: 'Two of: beak, wings, tail, feathers, feet, eyes.',
        tiedObjective: 'Identify parts of a bird',
      },
      {
        prompt: 'Why do you think birds have feathers?',
        type: 'multiple-choice',
        options: [
          'To stay warm and help them fly',
          'For decoration only',
          'Because they are cold-blooded',
          'To swim faster',
        ],
        correctIndex: 0,
        tiedObjective: 'Connect animal features to their functions',
      },
    ],
  },
  {
    activityId: 'eq-003', // Show and Tell
    tier: 'pre-k',
    questions: [
      {
        prompt: 'Hold up your favorite thing and tell us ONE thing you love about it.',
        type: 'show-and-tell',
        parentCheckRubric:
          'Child speaks while holding item. Any feeling word or reason passes.',
        tiedObjective: 'Practice speaking in front of an audience',
      },
      {
        prompt: 'Ask your grown-up ONE question about their favorite thing.',
        type: 'say-it',
        parentCheckRubric: 'Child forms a question (who/what/why/when).',
        tiedObjective: 'Take turns as speaker and listener',
      },
    ],
  },
  {
    activityId: 'eng-003', // Egg Drop
    tier: 'upper-elementary',
    questions: [
      {
        prompt:
          'Your egg survived from 1m but broke at 2m. Name ONE design change AND predict what it will do.',
        type: 'say-it',
        parentCheckRubric:
          'Specific change (more padding, parachute, crumple zone) + specific predicted outcome. Hypothesis-style.',
        tiedObjective: 'Iterate on a design and predict the effect of each change',
      },
      {
        prompt: 'Which design principle MOST helps survive an impact?',
        type: 'multiple-choice',
        options: [
          'Making the package as heavy as possible',
          'Slowing the fall AND cushioning the impact',
          'Painting it red',
          'Making it perfectly smooth',
        ],
        correctIndex: 1,
        tiedObjective: 'Identify physical principles that reduce impact force',
      },
    ],
  },
  {
    activityId: 'ss-001',
    tier: 'early-elementary',
    questions: [
      {
        prompt: 'On a map, which direction is usually at the TOP?',
        type: 'multiple-choice',
        options: ['East', 'West', 'North', 'South'],
        correctIndex: 2,
        tiedObjective: 'Read basic map conventions (compass rose, orientation)',
      },
      {
        prompt:
          'Draw a simple map with a START, a PATH, and a DESTINATION. Label them.',
        type: 'draw',
        parentCheckRubric:
          'All three elements present and labeled. Symbolic representation of route.',
        tiedObjective: 'Represent a route using map symbols',
      },
    ],
  },
  {
    activityId: 'phys-003', // Freeze Dance
    tier: 'pre-k',
    questions: [
      {
        prompt: 'When I say FREEZE, what do you do?',
        type: 'do-it',
        parentCheckRubric: 'Child stops moving completely and holds still for 3+ seconds.',
        tiedObjective: 'Practice impulse control by stopping on command',
      },
      {
        prompt: 'Dance FAST. Now dance SLOW.',
        type: 'do-it',
        parentCheckRubric: 'Clear tempo change between two modes.',
        tiedObjective: 'Match movement to tempo cues',
      },
    ],
  },
  {
    activityId: 'va-003', // Golden Hour Walk
    tier: 'upper-elementary',
    questions: [
      {
        prompt: 'Why is "golden hour" good for photos?',
        type: 'multiple-choice',
        options: [
          'The sun is directly overhead, so shadows disappear',
          'The low-angle sunlight is warm and soft, creating long shadows and glow',
          'There is no light at all',
          'Cameras work better in the dark',
        ],
        correctIndex: 1,
        tiedObjective: 'Explain how light quality changes throughout the day',
      },
      {
        prompt: 'Show me a photo you took at golden hour and describe the LIGHT.',
        type: 'show-and-tell',
        parentCheckRubric:
          'Child uses at least one light-quality word: warm, soft, glowing, long shadows, golden.',
        tiedObjective: 'Describe lighting qualities in photographic images',
      },
    ],
  },
  {
    activityId: 'eq-004', // Problem Solving Puppets
    tier: 'kinder',
    questions: [
      {
        prompt:
          'Two puppets both want the same toy. Have them SOLVE IT without fighting.',
        type: 'do-it',
        parentCheckRubric:
          'Any nonviolent solution: sharing, turn-taking, finding another toy, asking an adult. Process matters.',
        tiedObjective: 'Generate peaceful solutions to common conflicts',
      },
      {
        prompt: 'Name THREE ways friends can share.',
        type: 'say-it',
        parentCheckRubric:
          'Three distinct strategies (take turns, split in half, play together, timer). Variety over specificity.',
        tiedObjective: 'List concrete strategies for sharing',
      },
    ],
  },
];

/* ------------------------------------------------------------------ */
/* Lookup helpers                                                     */
/* ------------------------------------------------------------------ */

/**
 * Get the exit ticket for a specific activity + tier, if one exists.
 * Returns undefined when no ticket has been authored yet (which is the norm
 * today — see design doc for authoring roadmap).
 */
export function getExitTicket(
  activityId: string,
  tier: Tier,
): ExitTicket | undefined {
  return exitTickets.find(
    (t) => t.activityId === activityId && t.tier === tier,
  );
}

/** Total authored ticket count. Used by content-audit scripts. */
export const EXIT_TICKET_COUNT = exitTickets.length;
